import { StudentProblemResult } from '@yandex-int/k-common/typings';
//@ts-ignore
import formatTime from '@yandex-int/k-common/utils/format-time';
import b from 'b_';
import { H5, Popup, PopupType, Text } from 'platform-components';
import { POPUP_DIRECTIONS, POPUP_THEMES, POPUP_AXIS } from 'platform-components/constants';
import React, { useEffect, useRef } from 'react';
import { connect } from 'react-redux';

import { PastesIcon } from '@edu-frontend/modules/src/features/pastesHistory';

import { ClassesState } from 'common.components/classes/typings';
import * as lessonSelectors from 'common.components/lesson/selectors';
import { LessonStore } from 'common.components/lesson/typings';
import ProblemDescription from 'common.components/results/__cell-description/results__cell-description-table';
import { CurrentCellData } from 'common.components/results/typings';
import { getDisplayStatus, toKebabCase } from 'utils/get-problem-display-status';
import { getTeamIdFromPath } from 'utils/path';

import * as selectors from '../selectors';

import './results__cell-description.scss';

interface StoreProps {
    showPopup: boolean;
    cellDescriptionData: CurrentCellData | null;
    studentsOnline: Record<number, number | null>;
    problemResult: StudentProblemResult | null;
    studentName: string | null;
    shouldDisplayPoints: boolean;
    shouldDisplayAttempts: boolean;
}

interface OwnProps {
    owner?: HTMLDivElement | null;
}

interface Props extends OwnProps, StoreProps {}

const OFFSET_TOP = -24;

export const ResultsCellDescription = (props: Props) => {
    const {
        owner,
        showPopup,
        cellDescriptionData,
        studentsOnline,
        problemResult,
        studentName,
        shouldDisplayAttempts,
        shouldDisplayPoints,
    } = props;

    const popupRef = useRef<PopupType>(null);

    useEffect(() => {
        if (showPopup) {
            popupRef.current?.show();
        } else {
            popupRef.current?.hide();
        }
    }, [showPopup]);

    useEffect(() => {
        //@ts-ignore - EDUCATION-37077: [react-update]: fixme typings: Хочет HTMLDivElement вместo ReactNode
        popupRef.current?.setOwner(owner);
    }, [owner]);

    if (!cellDescriptionData || !problemResult) {
        return null;
    }

    const { studentId, problemId, problemNumber, displayParams = {} } = cellDescriptionData;
    const displayStatus = getDisplayStatus({
        problemStatus: problemResult.problem_status,
        maxAttempts: problemResult.max_attempts,
        attemptNumber: problemResult.attempt_number,
        answered: problemResult.answered,
        type: problemResult.type,
        time: problemResult.time ? formatTime(problemResult.time) : problemResult.time,
        criteriaStats: displayParams.criteriaStats,
    });
    const isStudentOnline = studentsOnline[studentId] === problemId;
    const mix = b('results', 'cell-description', { status: displayStatus });

    return (
        <Popup
            axis={POPUP_AXIS.END}
            direction={POPUP_DIRECTIONS.TOP}
            fitToScreen
            inLayer
            mix={mix}
            offsetLeft={owner ? -owner.offsetWidth : undefined}
            //@ts-ignore TODO: тайпинги для попапа https://st.yandex-team.ru/EDUCATION-11116
            offsetTop={OFFSET_TOP}
            // @ts-ignore
            ref={popupRef}
            selfclosable
            theme={POPUP_THEMES.DARK}
        >
            <div className="results__cell-description-title">
                <H5>{studentName}</H5>
                {isStudentOnline && <Text id="lessonResults.problemResultsStatus.hereNow" />}
            </div>
            <div className="results__cell-description-status">
                <Text
                    id="lessonResults.problemResultsStatus.description"
                    values={{
                        problem: problemNumber,
                        problemResultStatus: displayStatus,
                        problemType: problemResult?.type,
                    }}
                />
                {displayParams.criteriaStats && (
                    <span className="results__cell-description-status">
                        {displayParams.criteriaStats.correct}/{displayParams.criteriaStats.max}
                    </span>
                )}
                <span
                    className={b('results', 'cell-description-status-icon', {
                        status: toKebabCase(displayStatus),
                        type: problemResult?.type,
                    })}
                />
            </div>
            <ProblemDescription
                displayParams={displayParams}
                problemResult={problemResult}
                shouldDisplayAttempts={shouldDisplayAttempts}
                shouldDisplayPoints={shouldDisplayPoints}
            />
            {displayParams.hasPaste && (
                <div className="results__cell-description-paste">
                    <PastesIcon />
                    <Text id="pasteHistory.hasPastes" />
                </div>
            )}
        </Popup>
    );
};

const mapStateToProps = <T extends ClassesState & LessonStore>(storeState: T): StoreProps => {
    const data = selectors.getDescriptionPopupData(storeState);
    const studentId = data ? data.studentId : null;
    const problemId = data ? data.problemId : null;
    const teamId = getTeamIdFromPath();

    return {
        showPopup: selectors.getShouldShowDescriptionPopup(storeState),
        cellDescriptionData: data,
        studentName: selectors.getStudentNameById(storeState, { classId: teamId, studentId }),
        problemResult: selectors.getStudentResultByIds(storeState, { classId: teamId, studentId, problemId }),
        studentsOnline: selectors.getStudentsOnline(storeState),
        shouldDisplayPoints: lessonSelectors.getShouldDisplayPoints(storeState),
        shouldDisplayAttempts: lessonSelectors.getShouldDisplayAttempts(storeState),
    };
};

export default connect(mapStateToProps)(ResultsCellDescription);
